import { readResponseOrGate } from './apiGate';

const apiUrl = process.env.REACT_APP_API_URL;

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const encodedCredentials = btoa(`${username}:${password}`);
  return `Basic ${encodedCredentials}`;
}

/**
 * Resends a pending org invite by re-issuing it for the same email.
 * Returns { gate: true, body } on HTTP 402, otherwise { gate: false, ok, status, text }.
 */
export async function resendInvite(inviterUserId, orgId, email) {
  const response = await fetch(`${apiUrl}/api/org/members/invite`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({
      inviterUserId,
      orgId,
      email: (email || '').trim(),
    }),
  });

  const gateResult = await readResponseOrGate(response);
  if (gateResult.gate) {
    return gateResult;
  }

  const text = await gateResult.response.text();
  return {
    gate: false,
    ok: gateResult.response.ok,
    status: gateResult.response.status,
    text,
  };
}

/**
 * Revokes a pending org invite so it can no longer be accepted.
 * Returns { gate: true, body } on HTTP 402, otherwise { gate: false, ok, status, text }.
 */
export async function revokeInvite(inviteId, adminUserId, orgId) {
  const response = await fetch(`${apiUrl}/api/org/invites/${inviteId}/revoke`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({
      adminUserId,
      orgId,
    }),
  });

  const gateResult = await readResponseOrGate(response);
  if (gateResult.gate) {
    return gateResult;
  }

  const text = await gateResult.response.text();
  return {
    gate: false,
    ok: gateResult.response.ok,
    status: gateResult.response.status,
    text,
  };
}

/**
 * Fetches the pending org invites for a logged-in user (shown as an in-app banner).
 * Returns an array of { inviteId, token, orgId, orgName, role, expiresAt }.
 * Fails silently (returns []) on any network/parse error so the banner just stays hidden.
 */
export async function fetchPendingInvites(userId) {
  try {
    const response = await fetch(`${apiUrl}/api/user/${userId}/pending-invites`, {
      method: 'GET',
      headers: {
        Accept: '*/*',
        Authorization: getBasicAuthHeader(),
      },
    });

    if (!response.ok) {
      return [];
    }

    const data = await parseJsonSafely(response);
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch (_) {
    return [];
  }
}

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

/**
 * Looks up a pending org invite by its token for the public accept-invite page.
 * Returns { ok, status, data } — data holds the invite preview on 200, or an
 * error payload (if any) on 404/410.
 */
export async function previewInvite(token) {
  const response = await fetch(`${apiUrl}/api/org/invites/${token}`, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
  });

  const data = await parseJsonSafely(response);
  return {
    ok: response.ok,
    status: response.status,
    data,
  };
}

/**
 * Accepts a pending org invite. New accounts (accountExists === false) must pass
 * `password`; existing accounts must pass `userId` matching the invite email.
 * Returns { ok, status, data }.
 */
export async function acceptInvite({ token, password, userId }) {
  const response = await fetch(`${apiUrl}/api/org/invites/accept`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({
      token,
      ...(password ? { password } : {}),
      ...(userId ? { userId } : {}),
    }),
  });

  const data = await parseJsonSafely(response);
  return {
    ok: response.ok,
    status: response.status,
    data,
  };
}

/**
 * Declines a pending org invite so it can no longer be accepted.
 * Returns { ok, status, data }.
 */
export async function declineInvite({ token }) {
  const response = await fetch(`${apiUrl}/api/org/invites/decline`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ token }),
  });

  const data = await parseJsonSafely(response);
  return {
    ok: response.ok,
    status: response.status,
    data,
  };
}
